import { CoinsStacked01Icon } from '@repo/studio-design-system-ui-icons/finance-ecommerce/coins-stacked-01'
import { DotsVerticalIcon } from '@repo/studio-design-system-ui-icons/general/dots-vertical'
import { Cube04Icon } from '@repo/studio-design-system-ui-icons/shapes/cube-04'
import { ShieldTickIcon } from '@repo/studio-design-system-ui-icons/security/shield-tick'
import { User01Icon } from '@repo/studio-design-system-ui-icons/users/user-01'
import { Users01Icon } from '@repo/studio-design-system-ui-icons/users/users-01'
import { MenuItem, MenuItemText } from '@repo/studio-components/menu-item'
import { Button } from '@repo/studio-design-system-ui-button'
import { useIsSmallViewport } from '@repo/studio-hooks-dom/use-breakpoint'
import { ROUTES } from '@repo/studio-utils/routes'
import * as DropdownMenu from 'primitives/dropdown'
import { useStudioTranslate, useStudioConfig } from '@repo/studio-config/studio-config-context'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useFeatureIsOn } from 'growthbook'
import { tw } from 'utils/classnames'
import { FEATURE_HIDE_CONTENT_MODERATION } from '../../utils/features'

export function GatedEllipsisMenu({ onNavigate }: { onNavigate?: () => void }) {
  const isNsfwPlatform = useStudioConfig()?.isNsfwPlatform
  const { data: isLoggedIn } = useIsLoggedInQuery()

  if (isNsfwPlatform && !isLoggedIn) {
    return null
  }

  return <EllipsisMenu onNavigate={onNavigate} />
}

export function EllipsisMenu({ onNavigate }: { onNavigate?: () => void }) {
  const t = useStudioTranslate()
  const isSmallViewport = useIsSmallViewport()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const hideContentModeration = useFeatureIsOn(FEATURE_HIDE_CONTENT_MODERATION)
  const modelsCatalogRoute = useStudioConfig()?.routeOverrides?.modelsCatalog ?? ROUTES.MODELS.ROOT

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button iconOnly aria-label={'More menu'} size="sm" variant="ghost">
          <DotsVerticalIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={isSmallViewport ? 'top' : 'right'}
          align="end"
          className="z-cf-menu"
        >
          <section
            className={tw(
              'bg-gray-1000 rounded-2 shadow-3-smooth w-full max-w-52 lg:max-w-96 overflow-x-clip gap-1 border border-gray-800 p-[10px] *:w-full scroller-xs',
            )}
          >
            <DropdownMenu.Item asChild>
              <MenuItem asChild>
                <StudioLayoutLink
                  to={modelsCatalogRoute}
                  onClick={onNavigate}
                  className={tw('w-full outline-none')}
                >
                  <Cube04Icon />
                  <MenuItemText>{t('studio.common.layout.ellipsis_menu.ai_models')}</MenuItemText>
                </StudioLayoutLink>
              </MenuItem>
            </DropdownMenu.Item>
            {isLoggedIn ? (
              <>
                <StudioLayoutLink
                  className={tw('flex items-center justify-center outline-none')}
                  to={ROUTES.COINS.TOPUPS}
                >
                  <MenuItem className="w-full">
                    <CoinsStacked01Icon />
                    <MenuItemText>
                      {t('studio.common.layout.ellipsis_menu.auto_top_up')}
                    </MenuItemText>
                  </MenuItem>
                </StudioLayoutLink>
                {hideContentModeration ? null : (
                  <StudioLayoutLink
                    className={tw('flex items-center justify-center outline-none')}
                    to={ROUTES.CONTENT_MODERATION.ROOT}
                  >
                    <MenuItem className="w-full">
                      <ShieldTickIcon />
                      <MenuItemText>
                        {t('studio.common.layout.ellipsis_menu.content_moderation')}
                      </MenuItemText>
                    </MenuItem>
                  </StudioLayoutLink>
                )}
              </>
            ) : null}
            <DropdownMenu.Item asChild>
              <MenuItem asChild>
                <StudioLayoutLink
                  to={ROUTES.TEAM.ROOT}
                  reloadDocument
                  onClick={onNavigate}
                  className={tw('w-full outline-none')}
                >
                  <Users01Icon />
                  <MenuItemText>{t('studio.common.layout.ellipsis_menu.teams')}</MenuItemText>
                </StudioLayoutLink>
              </MenuItem>
            </DropdownMenu.Item>
            <DropdownMenu.Item asChild>
              <MenuItem asChild>
                <StudioLayoutLink
                  mask
                  to={ROUTES.ACCOUNT_SETTINGS.MY_ACCOUNT}
                  onClick={onNavigate}
                  className={tw('w-full outline-none')}
                >
                  <User01Icon />
                  <MenuItemText>{t('studio.common.layout.ellipsis_menu.my_account')}</MenuItemText>
                </StudioLayoutLink>
              </MenuItem>
            </DropdownMenu.Item>
          </section>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
